// Documento di un contenuto (versione 2): impostazioni della pagina e sezioni con
// stile, ognuna con i suoi blocchi. È tutto ciò che serve per impaginare il
// contenuto: si salva così com'è in ToolContents.Content e lo rende un unico
// renderer, senza codice scritto per la singola pagina.
//
//   {
//     "version": 2,
//     "page": { "title": "…", "description": "…", "image": "…", "agentNotes": "…" },
//     "sections": [
//       { "background": "soft", "width": "normal", "layout": "split", "align": "start",
//         "blocks": [ { "type": "richText", … }, { "type": "media", … } ] }
//     ]
//   }
//
// Un array di blocchi (formato 1) si legge come una sola sezione senza stile.
// Le regole devono restare allineate a EasyToolsAPI/Services/ToolContents/ToolContentDocument.cs.

import { BLOCKS_BY_KIND, ToolContentKind, ToolDetailBlock, parseToolDetail } from './blocks';

export type SectionBackground = 'plain' | 'soft' | 'brand' | 'dark';
export type SectionWidth = 'narrow' | 'normal' | 'wide';
/** stack: un blocco sotto l'altro. split: due colonne, i blocchi si affiancano a coppie. */
export type SectionLayout = 'stack' | 'split';
export type SectionAlign = 'start' | 'center';

export interface ContentSection {
  background?: SectionBackground;
  width?: SectionWidth;
  layout?: SectionLayout;
  align?: SectionAlign;
  blocks: ToolDetailBlock[];
}

export interface ContentPage {
  /** Titolo per i motori di ricerca e la scheda del browser (pagina pubblica). */
  title?: string;
  /** Descrizione per i motori di ricerca e le anteprime social (pagina pubblica). */
  description?: string;
  /** Immagine per le anteprime social (pagina pubblica). */
  image?: string;
  /** Indicazioni per il Desktop Agent: si salvano col contenuto ma non si mostrano mai. */
  agentNotes?: string;
}

export interface ToolContentDocument {
  version: 2;
  page: ContentPage;
  sections: ContentSection[];
}

export const PAGE_LIMITS = { title: 70, description: 160, agentNotes: 4000 } as const;

export const SECTION_BACKGROUNDS: { value: SectionBackground; label: string }[] = [
  { value: 'plain', label: 'Nessuno' },
  { value: 'soft', label: 'Tenue' },
  { value: 'brand', label: 'Colore' },
  { value: 'dark', label: 'Scuro' },
];
export const SECTION_WIDTHS: { value: SectionWidth; label: string }[] = [
  { value: 'narrow', label: 'Stretta' },
  { value: 'normal', label: 'Normale' },
  { value: 'wide', label: 'Piena' },
];
export const SECTION_LAYOUTS: { value: SectionLayout; label: string }[] = [
  { value: 'stack', label: 'Una colonna' },
  { value: 'split', label: 'Due colonne' },
];
export const SECTION_ALIGNS: { value: SectionAlign; label: string }[] = [
  { value: 'start', label: 'A sinistra' },
  { value: 'center', label: 'Al centro' },
];

export const createSection = (blocks: ToolDetailBlock[] = []): ContentSection => ({ blocks });

export const createDocument = (blocks: ToolDetailBlock[] = []): ToolContentDocument =>
  ({ version: 2, page: {}, sections: blocks.length ? [createSection(blocks)] : [] });

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isDocumentShape = (value: unknown): value is { page?: unknown; sections: unknown[] } =>
  isObject(value) && Array.isArray(value.sections);

/** Tiene solo i valori di stile conosciuti: un valore sconosciuto vale come predefinito. */
function cleanSection(raw: Record<string, unknown>, blocks: ToolDetailBlock[]): ContentSection {
  const pick = <T extends string>(value: unknown, allowed: { value: T }[]): T | undefined =>
    allowed.some(a => a.value === value) ? value as T : undefined;
  const section: ContentSection = { blocks };
  const background = pick(raw.background, SECTION_BACKGROUNDS);
  const width = pick(raw.width, SECTION_WIDTHS);
  const layout = pick(raw.layout, SECTION_LAYOUTS);
  const align = pick(raw.align, SECTION_ALIGNS);
  if (background && background !== 'plain') section.background = background;
  if (width && width !== 'normal') section.width = width;
  if (layout && layout !== 'stack') section.layout = layout;
  if (align && align !== 'start') section.align = align;
  return section;
}

function cleanPage(raw: unknown): ContentPage {
  if (!isObject(raw)) return {};
  const page: ContentPage = {};
  for (const key of ['title', 'description', 'image', 'agentNotes'] as const) {
    const value = raw[key];
    if (typeof value === 'string' && value.trim()) page[key] = value;
  }
  return page;
}

/**
 * Converte il contenuto salvato in documento. Accetta il formato 2, un array di
 * blocchi o { blocks: [...] }; il testo semplice diventa una sezione con un blocco
 * di testo, come fa parseToolDetail.
 */
export function parseToolDocument(raw?: string | null): ToolContentDocument {
  const text = (raw ?? '').trim();
  if (text.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(text);
      if (isDocumentShape(parsed)) {
        return {
          version: 2,
          page: cleanPage(parsed.page),
          sections: parsed.sections.filter(isObject).map(section =>
            cleanSection(section, Array.isArray(section.blocks) ? parseBlocks(section.blocks) : [])),
        };
      }
    } catch {
      // non era JSON valido: ci pensa parseToolDetail
    }
  }
  return createDocument(parseToolDetail(text));
}

const KNOWN_TYPES = new Set<string>(Object.values(BLOCKS_BY_KIND).flat());

/** I blocchi di una sezione: quelli di tipo sconosciuto si saltano, non rompono il documento. */
const parseBlocks = (blocks: unknown[]): ToolDetailBlock[] =>
  blocks.filter((b): b is ToolDetailBlock => isObject(b) && KNOWN_TYPES.has(String(b.type ?? '')));

/** Forma da salvare: stringa vuota se il documento non ha blocchi. */
export function serializeToolDocument(doc: ToolContentDocument): string {
  if (!documentBlocks(doc).length) return '';
  const page = cleanPage(doc.page);
  return JSON.stringify({
    version: 2,
    ...(Object.keys(page).length ? { page } : {}),
    sections: doc.sections
      .filter(section => section.blocks.length)
      .map(section => cleanSection(section as unknown as Record<string, unknown>, section.blocks)),
  });
}

/** Tutti i blocchi in ordine di lettura. */
export const documentBlocks = (doc: ToolContentDocument): ToolDetailBlock[] =>
  doc.sections.flatMap(section => section.blocks);

/** Il documento senza le parti che non vanno mostrate a chi legge (note per il Desktop Agent). */
export function publicDocument(doc: ToolContentDocument): ToolContentDocument {
  const { agentNotes: _notes, ...page } = doc.page;
  return { ...doc, page };
}

/**
 * Parser stretto per il JSON incollato: documento, array di blocchi, { blocks } o
 * un singolo blocco. Null se non è valido o contiene blocchi non ammessi in `kind`.
 */
export function tryParseDocument(text: string, kind?: ToolContentKind): ToolContentDocument | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }

  const allowed = kind ? new Set<string>(BLOCKS_BY_KIND[kind]) : KNOWN_TYPES;
  const validBlocks = (list: unknown): list is ToolDetailBlock[] =>
    Array.isArray(list) && list.every(b => isObject(b) && allowed.has(String(b.type ?? '')));

  if (isDocumentShape(parsed)) {
    if (!parsed.sections.every(s => isObject(s) && validBlocks(s.blocks))) return null;
    const doc = parseToolDocument(trimmed);
    return documentBlocks(doc).length ? doc : null;
  }
  const list = Array.isArray(parsed) ? parsed
    : isObject(parsed) && Array.isArray(parsed.blocks) ? parsed.blocks
      : isObject(parsed) && typeof parsed.type === 'string' ? [parsed]
        : null;
  if (!list?.length || !validBlocks(list)) return null;
  return createDocument(list.map(b => structuredClone(b)));
}
